import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import { CONFLICT_ALERTS_KEY } from './use-conflict-alerts';

import type {
  MitigateConflictAlertBody,
  AcceptWithJustificationBody,
  MarkFalsePositiveBody,
  ResolveConflictAlertBody,
  ReopenConflictAlertBody,
} from '../types';

function useInvalidateAlerts(): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: CONFLICT_ALERTS_KEY });
}

export function useMitigateConflictAlert() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: ({
      id,
      version,
      body,
    }: {
      id: string;
      version: number;
      body: MitigateConflictAlertBody;
    }) => api.mitigateConflictAlert(id, version, body),
    onSuccess: invalidate,
  });
}

export function useAcceptConflictAlert() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: ({
      id,
      version,
      body,
    }: {
      id: string;
      version: number;
      body: AcceptWithJustificationBody;
    }) => api.acceptConflictAlertWithJustification(id, version, body),
    onSuccess: invalidate,
  });
}

export function useMarkFalsePositive() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: ({
      id,
      version,
      body,
    }: {
      id: string;
      version: number;
      body: MarkFalsePositiveBody;
    }) => api.markConflictAlertFalsePositive(id, version, body),
    onSuccess: invalidate,
  });
}

export function useResolveConflictAlert() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: ({
      id,
      version,
      body,
    }: {
      id: string;
      version: number;
      body: ResolveConflictAlertBody;
    }) => api.resolveConflictAlert(id, version, body),
    onSuccess: invalidate,
  });
}

// Item 322 faceta a — reopen. invalidate atinge o prefixo ['conflict-alerts'],
// refrescando tanto a lista de Ativos quanto o Histórico infinito.
export function useReopenConflictAlert() {
  const invalidate = useInvalidateAlerts();
  return useMutation({
    mutationFn: ({
      id,
      version,
      body,
    }: {
      id: string;
      version: number;
      body: ReopenConflictAlertBody;
    }) => api.reopenConflictAlert(id, version, body),
    onSuccess: invalidate,
  });
}
